<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Border radius
      </h1>
      <p class="page-title__sub">
        Utility classes for border radii.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <p>
        Naming of border radius classes will change from project to project,
        depending on the radius sizes required. Where<code class="margin-right-5">{size}</code>
        appears below this will represent the variables you add.
      </p>
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class name</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.round</code>
              </td>
              <td>
                <code class="code__prop">border-radius: 100%;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-0</code>
              </td>
              <td>
                <code class="code__prop">border-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-t-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-top-right-radius: {size};</code>
                <code class="code__prop">border-top-left-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-t-0</code>
              </td>
              <td>
                <code class="code__prop">border-top-right-radius: 0;</code>
                <code class="code__prop">border-top-left-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-b-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-right-radius: {size};</code>
                <code class="code__prop">border-bottom-left-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-b-0</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-right-radius: 0;</code>
                <code class="code__prop">border-bottom-left-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-r-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-top-right-radius: {size};</code>
                <code class="code__prop">border-bottom-right-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-r-0</code>
              </td>
              <td>
                <code class="code__prop">border-top-right-radius: 0;</code>
                <code class="code__prop">border-bottom-right-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-l-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-top-left-radius: {size};</code>
                <code class="code__prop">border-bottom-left-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-l-0</code>
              </td>
              <td>
                <code class="code__prop">border-top-left-radius: 0;</code>
                <code class="code__prop">border-bottom-left-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-tr-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-top-right-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-tr-0</code>
              </td>
              <td>
                <code class="code__prop">border-top-right-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-tl-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-top-left-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-tl-0</code>
              </td>
              <td>
                <code class="code__prop">border-top-left-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-bl-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-left-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-bl-0</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-left-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-br-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-right-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-br-0</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-right-radius: 0;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-bl-{size}</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-left-radius: {size};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.radius-bl-0</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-left-radius: 0;</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Usage
        </h2>
      </header>
      <p>
        To set the border radius of an element, suffix the position
        <code class="margin-right-5">{-t | b | l | r}</code> and then the size
        <code>{-size}</code>, for example <code>.radius-t-10</code>.
      </p>
      <p>
        You can set a zero radius to elements in a similar way by replacing
        the size with none, for example <code>.radius-b-0</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="round..."&gt;&lt;/div&gt;
&lt;div class="radius-t-10..."&gt;&lt;/div&gt;
&lt;div class="radius-b-10..."&gt;&lt;/div&gt;
&lt;div class="radius-r-10..."&gt;&lt;/div&gt;
&lt;div class="radius-l-10..."&gt;&lt;/div&gt;
&lt;div class="radius-tr-10"&gt;&lt;/div&gt;
&lt;div class="radius-bl-10..."&gt;&lt;/div&gt;
&lt;div class="radius-0..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.round</code>
              <span class="preview__button round bg-tiara" />
            </div>
            <div class="flex flex-col">
              <code class="code inline-flex margin-bottom-5">.radius-t-10</code>
              <span class="preview__button radius-t-10 bg-tiara" />
            </div>
            <div class="flex flex-col">
              <code class="code inline-flex margin-bottom-5">.radius-b-10</code>
              <span class="preview__button radius-b-10 bg-tiara" />
            </div>
            <div class="flex flex-col">
              <code class="code inline-flex margin-bottom-5">.radius-r-10</code>
              <span class="preview__button radius-r-10 bg-tiara" />
            </div>
            <div class="flex flex-col">
              <code class="code inline-flex margin-bottom-5">.radius-l-10</code>
              <span class="preview__button radius-l-10 bg-tiara" />
            </div>
            <div class="flex flex-col">
              <code class="code inline-flex margin-bottom-5">.radius-tr-10</code>
              <span class="preview__button radius-tr-10 bg-tiara" />
            </div>
            <div class="flex flex-col">
              <code class="code inline-flex margin-bottom-5">.radius-bl-10</code>
              <span class="preview__button radius-bl-10 bg-tiara" />
            </div>
            <div class="flex flex-col">
              <code class="code inline-flex margin-bottom-5">.radius-0</code>
              <span class="preview__button radius-0 bg-tiara" />
            </div>
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'BrdRadius',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>
